import { useMemo, useState } from 'react';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import { Link, useParams, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Formik } from 'formik';
import { ActionGetAvailableRooms, ActionChangeRoom } from '../../../store/Actions/BookingAction';
import { ActionGetOrderByID } from '../../../store/Actions/OrderHistoryAction';
import { Select, Divider, Modal, Button, List, Tag, Spin, Alert, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { User, Phone, Mail, CreditCard, Calendar, Clock, DollarSign, FileText, Shield } from 'react-feather';
import './updateCheckIn.css';
import {
  useCheckInData,
  useAadhaarValidation,
  usePersonsInitialValues,
  useFormSubmission
} from './useCheckInHooks';

const { Option } = Select;

const UpdateCheckIn = () => {
  const { slug } = useParams();
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const pageValue = searchParams.get('page') ? searchParams.get('page') : 1;
  const bookingId = searchParams.get('bookingId') || null;
  const roomNameFromUrl = searchParams.get('roomName') || null;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isAvailableRoomsModalVisible, setIsAvailableRoomsModalVisible] = useState(false);
  const { availableRooms, availableRoomsLoading, availableRoomsError } = useSelector((state) => state.BookingManagement);
  const { loader: orderLoading } = useSelector((state) => state.OrderHistory);

  // Custom hooks
  const { patientData, orderDetail, roomSlot } = useCheckInData(slug);
  const { aadhaarError, setAadhaarError, validateAllPersons, validateArrivalInfo } = useAadhaarValidation();
  const { isProcessing, submitForm } = useFormSubmission(bookingId, pageValue);
  const { personsData, confirmationData, isLoading: personsLoading } = usePersonsInitialValues(orderDetail, patientData);
  const [currentRoomName, setCurrentRoomName] = useState(roomNameFromUrl);



  const defaultTimes = useMemo(() => ({
    date: moment().format('YYYY-MM-DD'),
    time: moment().format('HH:mm:ss'),
    arrivalDate: moment().format('DD-MM-YYYY'),
    arrivalTime: moment().format('HH:mm')
  }), [bookingId]);



  const handleChangeRoom = () => {
    if (!bookingId) {
      message.error('Booking ID not found');
      return;
    }

    // Find the specific room detail for this customer to get roomTypeId
    const customerId = orderDetail?.customerId?._id || patientData?._id;
    const roomDetail = orderDetail?.additionalInfo?.roomDetails?.find(rd =>
      rd.customerId === customerId
    ) || orderDetail?.additionalInfo?.roomDetails?.[0];

    const admissionDate = orderDetail?.additionalInfo?.startDate;
    const dischargeDate = orderDetail?.additionalInfo?.endDate;

    const formattedAdmissionDate = admissionDate ? moment(admissionDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : null;
    const formattedDischargeDate = dischargeDate ? moment(dischargeDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : null;

    dispatch(ActionGetAvailableRooms({
      bookingId: bookingId,
      isUpgrade: false,
      admissionDate: formattedAdmissionDate,
      dischargeDate: formattedDischargeDate,
      roomTypeId: roomDetail?.roomTypeId
    }));
    setIsAvailableRoomsModalVisible(true);
  };

  const handleRoomSelection = (room) => {
    Modal.confirm({
      title: 'Confirm Room Change',
      content: (
        <div>
          <p>Are you sure you want to change to <strong>{room.roomNo}</strong>?</p>
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>This will move the booking to the selected room.</p>
        </div>
      ),
      zIndex: 10001,
      okText: 'Confirm Change',
      cancelText: 'Cancel',
      onOk: () => {
        dispatch(ActionChangeRoom({
          bookingId,
          newRoomId: room._id
        }, (response, error) => {
          if (error) {
            message.error(error?.response?.data?.message || 'Failed to change room');
          } else {
            message.success(`Room changed successfully to ${room.roomNo}!`);
            setCurrentRoomName(room.roomNo);
            setIsAvailableRoomsModalVisible(false);
            // Refresh order detail to show updated room info
            dispatch(ActionGetOrderByID({ Query: `/${slug}` }));
          }
        }));
      }
    });
  };

  const InfoCard = ({ icon: Icon, label, value, color = '#2e37a4' }) => {
    return (
      <div className="info-card-item">
        <div className="info-icon-wrapper" style={{ background: `${color}15`, color }}>
          <Icon size={18} />
        </div>
        <div className="info-content">
          <span className="info-label">{label}</span>
          <span className="info-value">{value || '---'}</span>
        </div>
      </div>
    );
  };

  const amountRaw = orderDetail?.amount ?? roomSlot?.[0]?.orderId?.amount ?? 0;
  const amount = Number(amountRaw) || 0;
  const discountType =
    orderDetail?.discountType ??
    orderDetail?.additionalInfo?.discountType ??
    roomSlot?.[0]?.orderId?.discountType ??
    '';
  const discountRaw =
    orderDetail?.discount ??
    orderDetail?.additionalInfo?.discount ??
    roomSlot?.[0]?.orderId?.discount ??
    0;
  const discount = Number(discountRaw) || 0;
  const isDiscountApplied =
    orderDetail?.applied ??
    orderDetail?.additionalInfo?.applied ??
    roomSlot?.[0]?.orderId?.applied ??
    true;
  const discountValue =
    isDiscountApplied
      ? (discountType?.toLowerCase() === 'percentage' ? amount * (discount / 100) : discount)
      : 0;
  const payableAmount = Math.max(amount - discountValue, 0);
  const paidAmountRaw =
    orderDetail?.paidAmount ??
    orderDetail?.additionalInfo?.paidAmount ??
    roomSlot?.[0]?.orderId?.paidAmount ??
    0;
  const paidAmount = Number(paidAmountRaw) || 0;
  const pendingAmount = Math.max(payableAmount - paidAmount, 0);

  return (
    <>
      <Header />
      <Sidebar />
      <div className="update-checkin-container">
        <div className="page-wrapper">
          <div className="content">
            {/* Breadcrumb */}
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/room-slot">Room Slot</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <i className="feather-chevron-right">
                        <FeatherIcon icon="chevron-right" />
                      </i>
                    </li>
                    <li className="breadcrumb-item active">Update Check-In</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="row">
              <div className="col-sm-12">
                {orderLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px', color: '#64748b' }}>Loading check-in details...</p>
                  </div>
                ) : (
                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      customerName: orderDetail?.customerId?.name || patientData?.name || roomSlot?.[0]?.customerName || '-',
                      registrationNo: orderDetail?.customerId?.registrationNo || patientData?.registrationNo || roomSlot?.[0]?.customerId?.registrationNo || '-',
                      phoneNo: orderDetail?.customerId?.phoneNo || patientData?.phoneNo || roomSlot?.[0]?.customerId?.phoneNo || '-',
                      email: orderDetail?.customerId?.email || patientData?.email || roomSlot?.[0]?.customerId?.email || '-',
                      gender: orderDetail?.customerId?.gender || patientData?.gender || '-',
                      birthDate: orderDetail?.customerId?.birthDate ? moment(orderDetail.customerId.birthDate).format('DD-MM-YYYY') : (patientData?.birthDate ? moment(patientData.birthDate).format('DD-MM-YYYY') : '-'),
                      age: patientData?.age || '-',
                      address: patientData?.address || '-',
                      education: patientData?.education || '-',
                      height: patientData?.height || '-',
                      weight: patientData?.weight || '-',
                      oldCasePaperNo: patientData?.oldCasePaperNo || '-',
                      bookingFor: orderDetail?.bookingFor || '-',
                      startDate: orderDetail?.additionalInfo?.startDate || '-',
                      endDate: orderDetail?.additionalInfo?.endDate || '-',
                      totalDays: orderDetail?.additionalInfo?.totalDays || '-',
                      serviceName: orderDetail?.additionalInfo?.serviceName ||
                        orderDetail?.additionalInfo?.programmeData?.[0]?.programmeName || '-',
                      programmeName: orderDetail?.additionalInfo?.programmeData?.[0]?.programmeName || '-',
                      perDayPrice: orderDetail?.additionalInfo?.programmeData?.[0]?.perDayPrice || '-',
                      availableDays: orderDetail?.additionalInfo?.roomDetails?.[0]?.availableCount || '-',
                      roomNo: currentRoomName || '-',
                      roomType: orderDetail?.additionalInfo?.roomDetails?.[0]?.roomTypeName || roomSlot?.[0]?.roomType || '-',
                      occupancyMode: orderDetail?.additionalInfo?.roomDetails?.[0]?.occupancyMode || '-',
                      checkInDate: defaultTimes.date,
                      checkInTime: defaultTimes.time,
                      amount: orderDetail?.amount || roomSlot?.[0]?.orderId?.amount || '-',
                      paidAmount: orderDetail?.paidAmount || '-',
                      paymentStatus: orderDetail?.paymentStatus || '-',
                      paymentMode: orderDetail?.paymentMode || roomSlot?.[0]?.orderId?.paymentMode || '-',
                      orderStatus: orderDetail?.orderStatus || '-',
                      arrivingFrom: confirmationData?.arrivingFrom || defaultTimes.arrivalDate,
                      arrivalTime: confirmationData?.arrivalTime || defaultTimes.arrivalTime,
                      persons: personsData,
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                      const arrivalError = validateArrivalInfo(values.arrivingFrom, values.arrivalTime);
                      if (arrivalError) {
                        setAadhaarError(arrivalError);
                        setSubmitting(false);
                        return;
                      }

                      const aadhaarErrors = validateAllPersons(values.persons);
                      if (aadhaarErrors.length > 0) {
                        setAadhaarError(aadhaarErrors.join('; '));
                        setSubmitting(false);
                        return;
                      }
                      setAadhaarError('');
                      const result = await submitForm(values.persons, values.arrivingFrom, values.arrivalTime);
                      if (result.success) {
                        message.success(result.message);
                        // Redirect to pending-history with phoneNo filter
                        const phoneNo = values.phoneNo !== '-' ? values.phoneNo : '';
                        navigate(`/pending-history?phoneNo=${phoneNo}`);
                      } else {
                        message.error(result.error);
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
                      <div>
                        {/* Header Card */}
                        <div className="checkin-header-card">
                          <div className="header-content">
                            <div>
                              <h2 className="header-title">Update Check-In Information</h2>
                              <p className="header-subtitle">
                                Complete the check-in process for {values?.customerName}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Customer Information Section */}
                        <div className="section-card">
                          <div className="section-header">
                            <div className="section-icon-wrapper blue">
                              <User size={20} />
                            </div>
                            <h3 className="section-title">Customer Information</h3>
                          </div>
                          <div className="info-grid">
                            <InfoCard icon={User} label="Customer Name" value={values?.customerName} color="#2e37a4" />
                            <InfoCard icon={Phone} label="Mobile Number" value={values?.phoneNo} color="#10b981" />
                            <InfoCard icon={Mail} label="Email Address" value={values?.email} color="#f59e0b" />
                            <InfoCard icon={User} label="Gender" value={values?.gender} color="#ec4899" />
                            <InfoCard icon={Calendar} label="Date of Birth" value={values?.birthDate} color="#3b82f6" />
                            <InfoCard icon={FileText} label="Registration No" value={values?.registrationNo} color="#8b5cf6" />
                          </div>
                        </div>

                        {/* Room & Booking Details Section */}
                        <div className="section-card">
                          <div className="section-header">
                            <div className="section-icon-wrapper green">
                              <CreditCard size={20} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <h3 className="section-title">Room & Booking Details</h3>
                              <Button
                                type="primary"
                                size="small"
                                ghost
                                icon={<FeatherIcon icon="repeat" size={14} style={{ marginRight: 6 }} />}
                                onClick={handleChangeRoom}
                                style={{ borderRadius: '6px', height: '32px', display: 'flex', alignItems: 'center' }}
                              >
                                Change Room
                              </Button>
                            </div>
                          </div>
                          <div className="info-grid">
                            <InfoCard icon={CreditCard} label="Room Name" value={values?.roomNo} color="#2e37a4" />
                            <InfoCard icon={FileText} label="Room Type" value={values?.roomType} color="#10b981" />
                            <InfoCard icon={Calendar} label="Check-In Date" value={values?.checkInDate} color="#f59e0b" />
                            <InfoCard icon={Clock} label="Check-In Time" value={values?.checkInTime} color="#8b5cf6" />
                            <InfoCard icon={DollarSign} label="Payment Mode" value={values?.paymentMode} color="#ec4899" />
                          </div>
                        </div>

                        {/* Booking Information Section */}
                        {orderDetail && (
                          <div className="section-card">
                            <div className="section-header">
                              <div className="section-icon-wrapper orange">
                                <Calendar size={20} />
                              </div>
                              <h3 className="section-title">Booking Information</h3>
                            </div>
                            <div className="info-grid">
                              <InfoCard icon={FileText} label="Booking For" value={values?.bookingFor} color="#2e37a4" />
                              <InfoCard icon={FileText} label={values?.bookingFor} value={values?.serviceName} color="#10b981" />
                              {values?.bookingFor === 'Programme' && values?.perDayPrice !== '-' && (
                                <InfoCard icon={DollarSign} label="Per Day Price" value={`₹${values?.perDayPrice}`} color="#06b6d4" />
                              )}
                              <InfoCard icon={Calendar} label="Start Date" value={values?.startDate} color="#3b82f6" />
                              <InfoCard icon={Calendar} label="End Date" value={values?.endDate} color="#f59e0b" />
                              <InfoCard icon={Clock} label="Total Days" value={`${values?.totalDays} days`} color="#8b5cf6" />
                              {values?.availableDays !== '-' && (
                                <InfoCard icon={Calendar} label="Available Days" value={values?.availableDays} color="#14b8a6" />
                              )}
                              <InfoCard icon={FileText} label="Occupancy Mode" value={values?.occupancyMode} color="#ec4899" />
                              <InfoCard icon={DollarSign} label="Total Amount" value={`₹${values?.amount}`} color="#10b981" />
                              <InfoCard icon={DollarSign} label="Paid Amount" value={`₹${values?.paidAmount}`} color="#3b82f6" />
                              <InfoCard icon={Shield} label="Payment Status" value={values?.paymentStatus} color={values?.paymentStatus === 'Completed' ? '#10b981' : '#f59e0b'} />
                              <InfoCard icon={FileText} label="Order Status" value={values?.orderStatus} color={values?.orderStatus === 'Completed' ? '#10b981' : '#f59e0b'} />
                            </div>
                          </div>
                        )}

                        {/* Guest Details Section */}
                        {orderDetail?.additionalInfo?.guestDetails?.length > 0 && (
                          <div className="section-card">
                            <div className="section-header">
                              <div className="section-icon-wrapper blue">
                                <User size={20} />
                              </div>
                              <h3 className="section-title">Guest Details ({orderDetail.additionalInfo.guestDetails.length})</h3>
                            </div>
                            {orderDetail.additionalInfo.guestDetails.map((guest, index) => (
                              <div key={index} style={{ marginBottom: index < orderDetail.additionalInfo.guestDetails.length - 1 ? '20px' : '0', paddingBottom: index < orderDetail.additionalInfo.guestDetails.length - 1 ? '20px' : '0', borderBottom: index < orderDetail.additionalInfo.guestDetails.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', marginBottom: '12px' }}>Guest {index + 1}: {guest.name}</h4>
                                <div className="info-grid">
                                  <InfoCard icon={User} label="Name" value={guest.name} color="#2e37a4" />
                                  <InfoCard icon={User} label="Gender" value={guest.gender} color="#ec4899" />
                                  <InfoCard icon={Mail} label="Email" value={guest.email} color="#f59e0b" />
                                  <InfoCard icon={Phone} label="Phone" value={guest.phoneNo} color="#10b981" />
                                  <InfoCard icon={Calendar} label="Birth Date" value={guest.birthDate} color="#3b82f6" />
                                  <InfoCard icon={FileText} label="Registration No" value={guest.registrationNo || '---'} color="#8b5cf6" />
                                  <InfoCard icon={FileText} label="Booking For" value={guest.guestbookingFor} color="#06b6d4" />
                                  {guest.solutionData?.length > 0 && (
                                    <InfoCard icon={FileText} label="Solution" value={guest.solutionData[0].solutionName} color="#14b8a6" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Aadhaar & Mediclaim Collection Section */}
                        <div className="section-card">
                          <div className="section-header">
                            <div className="section-icon-wrapper orange">
                              <Shield size={20} />
                            </div>
                            <h3 className="section-title">Aadhaar & Mediclaim Details</h3>
                          </div>
                          <div className="row mb-4">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label className="form-label">Arriving From <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter city/location"
                                  value={values.arrivingFrom}
                                  onChange={(e) => setFieldValue('arrivingFrom', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label className="form-label">Arrival Time <span className="text-danger">*</span></label>
                                <input
                                  type="time"
                                  className="form-control"
                                  value={values.arrivalTime}
                                  onChange={(e) => setFieldValue('arrivalTime', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          {aadhaarError && (
                            <div className="alert alert-danger" role="alert">
                              {aadhaarError}
                            </div>
                          )}
                          <div className="persons-container">
                            {values.persons.map((person, index) => (
                              <div key={index} className={`person-card ${person.type === 'main' ? 'main-customer' : 'guest-card'}`}>
                                <div className="person-header">
                                  <div className="person-icon">
                                    {person.type === 'main' ? <User size={20} /> : <User size={18} />}
                                  </div>
                                  <h4 className="person-name">
                                    {person.type === 'main' ? 'Main Customer' : `Guest ${index}`}: {person.name}
                                  </h4>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="form-group">
                                      <label className="form-label">Aadhaar Number <span className="text-danger">*</span></label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter 12-digit Aadhaar number"
                                        value={person.aadhaarCardNumber}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                                          setFieldValue(`persons[${index}].aadhaarCardNumber`, value);
                                        }}
                                        maxLength={12}
                                      />

                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="form-group">
                                      <label className="form-label">Mediclaim Required <span className="text-danger">*</span></label>
                                      <Select
                                        value={person.mediClaim}
                                        onChange={(value) => {
                                          setFieldValue(`persons[${index}].mediClaim`, value);
                                        }}
                                        className="custom-select"
                                        style={{ width: '100%', height: '45px' }}
                                      >
                                        <Option value="No">No</Option>
                                        <Option value="Yes">Yes</Option>
                                      </Select>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="form-group">
                                      <label className="form-label">Emergency Contact Number <span className="text-danger">*</span></label>
                                      <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="Enter emergency contact number"
                                        value={person.emergencyNumber}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                          setFieldValue(`persons[${index}].emergencyNumber`, value);
                                        }}
                                        maxLength={10}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="form-group">
                                      <label className="form-label">Vehicle Number</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter vehicle number"
                                        value={person.vehicleNo}
                                        onChange={(e) => {
                                          setFieldValue(`persons[${index}].vehicleNo`, e.target.value.toUpperCase());
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {person.mediClaim === 'Yes' && (
                                  <>
                                    <Divider style={{ margin: '16px 0' }} />
                                    <div className="mediclaim-details">
                                      <h5 className="mediclaim-subtitle">Mediclaim Information</h5>
                                      <div className="row">
                                        <div className="col-md-6">
                                          <div className="form-group">
                                            <label className="form-label">Insurance Company</label>
                                            <input type="text" className="form-control" value={person.insuranceCompany} onChange={(e) => setFieldValue(`persons[${index}].insuranceCompany`, e.target.value)} />
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="form-group">
                                            <label className="form-label">Policy Number</label>
                                            <input type="text" className="form-control" value={person.policyNumber} onChange={(e) => setFieldValue(`persons[${index}].policyNumber`, e.target.value)} />
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="form-group">
                                            <label className="form-label">Policy Holder Name</label>
                                            <input type="text" className="form-control" value={person.policyHolderName} onChange={(e) => setFieldValue(`persons[${index}].policyHolderName`, e.target.value)} />
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="form-group">
                                            <label className="form-label">Claim Amount</label>
                                            <input type="number" className="form-control" value={person.claimAmount} onChange={(e) => setFieldValue(`persons[${index}].claimAmount`, e.target.value)} />
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="form-group">
                                            <label className="form-label">Contact Number</label>
                                            <input type="tel" className="form-control" value={person.contactNumber} onChange={(e) => setFieldValue(`persons[${index}].contactNumber`, e.target.value)} />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Payment Summary Card */}
                        {!["Programme"]?.includes(values?.bookingFor) && <div className="payment-summary-card">
                          <div className="summary-header">
                            <DollarSign size={24} />
                            <h3>Payment Summary</h3>
                          </div>
                          <div className="summary-content">
                            <div className="summary-row">
                              <span className="summary-label">Total Booking Amount</span>
                              <span className="summary-value primary">₹{amount}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">
                                Discount
                                {discountType ? ` (${discountType}${discountType.toLowerCase() === 'percentage' ? ` ${discount}%` : ''})` : ''}
                                {!isDiscountApplied ? ' (Not Applied)' : ''}
                              </span>
                              <span className="summary-value" style={{ color: '#f97316', fontWeight: 600 }}>
                                ₹{discountValue}
                              </span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Payable Amount</span>
                              <span className="summary-value" style={{ color: '#0ea5e9', fontWeight: 600 }}>₹{payableAmount}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Amount Paid</span>
                              <span className="summary-value" style={{ color: '#10b981', fontWeight: 600 }}>₹{paidAmount}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Balance Due</span>
                              <span className="summary-value" style={{ color: pendingAmount > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>₹{pendingAmount}</span>
                            </div>
                            <Divider style={{ margin: '16px 0' }} />
                            <div className="summary-row">
                              <span className="summary-label">Payment Method</span>
                              <span className="summary-value">{values?.paymentMode}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Status</span>
                              <span className="summary-value" style={{ color: values?.paymentStatus === 'Completed' ? '#10b981' : '#f59e0b', fontWeight: 600 }}>{values?.paymentStatus}</span>
                            </div>
                          </div>
                        </div>}

                        {/* Action Buttons */}
                        <div className="action-buttons">
                          <button type="button" onClick={() => (window.location.href = `/room-slot?page=${pageValue}`)} className="btn-cancel">Cancel</button>
                          <button type="submit" onClick={handleSubmit} className="btn-submit" disabled={isSubmitting || isProcessing}>
                            {(isSubmitting || isProcessing) ? 'Processing...' : 'Complete Check-In'}
                          </button>
                        </div>
                      </div>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Available Rooms Modal */}
      <Modal
        title="Change Available Room"
        open={isAvailableRoomsModalVisible}
        onCancel={() => setIsAvailableRoomsModalVisible(false)}
        footer={[<Button key="close" onClick={() => setIsAvailableRoomsModalVisible(false)}>Close</Button>]}
        width={600}
        centered
        zIndex={10000}
      >
        {availableRoomsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /><p style={{ marginTop: '16px', color: '#64748b' }}>Loading available rooms...</p></div>
        ) : availableRoomsError ? (
          <Alert message="Error" description={availableRoomsError} type="error" showIcon />
        ) : availableRooms && availableRooms.length > 0 ? (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <List
              dataSource={availableRooms}
              renderItem={(room) => (
                <List.Item
                  style={{ padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px' }}
                  actions={[
                    <Button key="select" type="primary" onClick={() => handleRoomSelection(room)}>
                      Select
                    </Button>
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, color: '#0f172a', fontWeight: 600 }}>{room.roomNo}</h4>
                      <Tag color="blue">Available</Tag>
                    </div>
                    <div style={{ marginTop: '6px', color: '#64748b', fontSize: '0.9rem' }}>
                      <span>{room.roomTypeName || 'Room Type'}</span>
                      {room.occupancyMode ? <span> • {room.occupancyMode}</span> : null}
                      {room.pricePerDay ? <span> • ₹{room.pricePerDay}/day</span> : null}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        ) : (
          <Alert message="No Available Rooms" description="There are no other available rooms for this category and date range." type="info" showIcon />
        )}
      </Modal>
    </>
  );
};

export default UpdateCheckIn;
